import * as React from "react";
import type {AriaSelectProps} from "@react-types/select";
import {useSelectState} from "react-stately";
import {useSelect, HiddenSelect, useButton, useFocusRing} from "react-aria";
import {ChevronUpDownIcon, MagnifyingGlassIcon} from "@heroicons/react/20/solid";
import {ListBox} from "./ListBox";
import {Popover} from "./Popover";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {useHover} from "@react-aria/interactions";
import {InputSetToNull} from "./Icons";
import {FieldButton} from "./DateRangePicker";
import {useEffect, useRef, useState} from "react";


export {Item} from "react-stately";

interface SelectProps<T> extends AriaSelectProps<T> {
    variant?: 'outlined' | 'embedded'
    startIcon?: React.ReactNode;
    renderChevron?: boolean;
    isReadOnly?: boolean;
    className?: string;
    allowsSearch?: boolean;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    fullWidth?: boolean;
}

export function Select<T extends object>(props: SelectProps<T>) {

    const {t} = useTranslation();

    // Create state based on the incoming props
    const state = useSelectState(props);

    // Track trigger width for popover sizing
    const [triggerWidth, setTriggerWidth] = useState<number>(0);

    // Get props for child elements from useSelect
    const ref = useRef(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const {labelProps, triggerProps, valueProps, menuProps} = useSelect(props, state, ref);

    // Get props for the button based on the trigger props from useSelect
    const {buttonProps, isPressed} = useButton(triggerProps, ref);
    const {focusProps, isFocusVisible} = useFocusRing();
    const {hoverProps, isHovered} = useHover({});

    const {
        variant = 'outlined',
        label,
        isRequired,
        isDisabled,
        isReadOnly,
        placeholder,
        startIcon,
        renderChevron = true,
        className,
        allowsSearch = false,
        searchValue = '',
        onSearchChange,
        searchPlaceholder,
        fullWidth = false,
    } = props;
    const placeholderText = placeholder ? placeholder : '';

    const isEmbedded = variant === 'embedded';
    const variantClass = `field-${variant}`;

    // Focus search input when menu opens
    useEffect(() => {
        if (state.isOpen && allowsSearch && searchInputRef.current) {
            // Small delay to ensure popover is rendered
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 50);
        }
    }, [state.isOpen, allowsSearch]);

    // Clear search when menu closes
    useEffect(() => {
        if (!state.isOpen && onSearchChange) {
            onSearchChange('');
        }
    }, [state.isOpen, onSearchChange]);

    // Measure trigger width when component mounts or when popover opens
    useEffect(() => {
        if (ref.current) {
            const updateWidth = () => {
                if (ref.current) {
                    const inputElement = ref.current as HTMLInputElement;
                    const width = inputElement.offsetWidth;
                    if (width) {
                        setTriggerWidth(width);
                    }
                }
            };

            // Update immediately
            updateWidth();

            // Update when popover opens (in case of layout changes)
            if (state.isOpen) {
                updateWidth();
            }

            // Add resize listener
            window.addEventListener('resize', updateWidth);
            return () => window.removeEventListener('resize', updateWidth);
        }
    }, [state.isOpen]);

    return (
        <div className={clsx(
            'inline-flex flex-col relative min-w-[180px]',
            {
                'w-full': fullWidth
            },
            className)}>

            {label && (
                <label {...labelProps} className={`field-label ${isDisabled ? 'field-disabled' : ''}`}>
                    {label}
                    {isRequired && <span className={'field-required'}>*</span>}
                </label>
            )}

            <HiddenSelect state={state} triggerRef={ref} label={props.label} name={props.name}/>
            <button {...buttonProps} {...hoverProps} {...focusProps} ref={ref}
                    className={clsx(
                        'field-base',
                        variantClass,
                        'items-center justify-between',
                        isFocusVisible && 'border-pink-500',
                        !isFocusVisible && 'border-gray-300',
                        state.isOpen ? 'bg-gray-100' : 'bg-inherit',
                        isHovered && 'field-hovered',
                        isDisabled && 'field-disabled',
                        isEmbedded && isPressed && 'field-pressed'
                    )}>

                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {startIcon && <span className="icon-start flex-shrink-0">{startIcon}</span>}

                    <span {...valueProps}
                          className={clsx(
                              `${variantClass} sm:text-sm/6 truncate`
                          )}>
                      {state.selectedItem ? state.selectedItem.rendered : placeholderText}
                    </span>
                </div>

                <div className="flex items-center space-x-1">
                    {!isDisabled && !isReadOnly && state.selectedKey && (
                        <FieldButton onPress={() => state.setSelectedKey(null)} aria-label="Reset selection">
                            <InputSetToNull className="text-gray-700 w-3.5 h-3.5"/>
                        </FieldButton>
                    )}

                    {(!isReadOnly && renderChevron) && (
                        <ChevronUpDownIcon className={`w-5 h-5 flex-shrink-0 ${
                            isFocusVisible ? "text-pink-500" : "text-gray-500"
                        }`}
                        />
                    )}

                    {isReadOnly && (
                        <span className={'text-xs text-gray-500 rounded-md bg-gray-200 px-2 py-1'}>
                            {t('components.select.readOnly')}
                        </span>
                    )}
                </div>

            </button>
            {!isReadOnly && state.isOpen && (
                <Popover state={state} triggerRef={ref} placement="bottom start">
                    <div className="flex flex-col" style={{ minWidth: triggerWidth ? `${triggerWidth}px` : 'auto' }}>
                        {allowsSearch && (
                            <div className="p-2 border-b border-gray-200 bg-inherit sticky top-0 z-10">
                                <div className="relative">
                                    <MagnifyingGlassIcon
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchValue}
                                        onChange={(e) => onSearchChange?.(e.target.value)}
                                        placeholder={searchPlaceholder || t('components.select.searchPlaceholder')}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                            // Prevent select from closing when typing
                                            e.stopPropagation();
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="overflow-auto">
                            <ListBox {...menuProps} state={state}/>
                        </div>
                    </div>
                </Popover>
            )}
        </div>
    );
}